import api from 'api';
import Router from 'next/router';
import { useDispatch, useReduxState } from 'hooks';
import { updateAttributeRequiredPasswords } from 'lib/customAttributes';
import { ModalButtonWithForm } from 'components/common';
import { UserAttributesAttributeForm } from '.';

import type { UserCustomAttribute } from 'shared-types';
import type { GetUserCustomAttributesRes } from 'shared-types/responses';
import type { UserAttributesAttributeFormState } from './UserAttributesAttributeForm';

interface Props {
  updateData: () => Promise<void>;
  userCustomAttributes: GetUserCustomAttributesRes['userCustomAttributes'];
}

const UserAttributesAdd = ({ updateData, userCustomAttributes }: Props) => {
  const { dispatch } = useDispatch();

  const { messages } = useReduxState();

  const handleSubmit = async (values: UserAttributesAttributeFormState) => {
    const data = {
      appliesTo: values.appliesTo,
      conditions: values.conditions,
      description: values.description,
      descriptionTranslations: values.descriptionTranslations,
      excludeFromExcelExport: values.excludeFromExcelExport,
      isEnabled: values.isEnabled,
      isRequired: values.isRequired,
      key: values.key,
      max: values.max,
      min: values.min,
      permissions: values.permissions,
      showBeforeRegistration: values.showBeforeRegistration,
      title: values.title,
      titleTranslations: values.titleTranslations,
      type: values.type,
      validation: values.validation
        ? {
            errorMessage: values.validation.errorMessage,
            errorMessageTranslations:
              values.validation.errorMessageTranslations,
            matchList: values.validation.matchList
              ?.split('\n')
              .filter((option) => option.trim())
              .map((option) => option),
            matchListEnableEditing: values.validation.matchListEnableEditing,
            matchRegex: values.validation.matchRegex,
            requiredPasswords:
              values.validation.requiredPasswords?.map((requiredPassword) => ({
                passwordIdentifier: requiredPassword.passwordIdentifier,
                title: requiredPassword.title,
                titleTranslations: requiredPassword.titleTranslations,
                valueMatchRegex: requiredPassword.valueMatchRegex
              })) || [],
            validateFunction: values.validation.validateFunction
          }
        : null,
      ...(values.type === 'ArrayWithOptions' ||
      values.type === 'StringWithOptions'
        ? {
            optionGroups: [{ conditions: [], options: [], sort: 'manual' }]
          }
        : {})
    };

    if (values.validation?.requiredPasswords?.length) {
      await updateAttributeRequiredPasswords(
        values.validation.requiredPasswords
      );
    }

    const response = await api.post('/admin/user-custom-attributes', data);

    const newAttributeId = response.data?.newAttributeId;

    await updateData();

    dispatch.addNotice({
      text: messages.attributesAddSuccess,
      type: 'success'
    });

    Router.push(
      '/admin/settings/user-attributes/[attributeId]',
      `/admin/settings/user-attributes/${newAttributeId}`
    );
  };

  const initialValues = {
    appliesTo: {
      admin: false,
      guest: false,
      user: false
    },
    conditions: null,
    description: '',
    descriptionTranslations: null,
    excludeFromExcelExport: false,
    isEnabled: false,
    isRequired: { value: false },
    key: '',
    max: 100,
    min: 0,
    optionGroups: null,
    permissions: {
      edit: {
        admin: { value: false, conditions: [] },
        superadmin: { value: false, conditions: [] },
        user: { value: false, conditions: [] }
      },
      editSettings: {
        admin: { value: false, conditions: [] },
        superadmin: { value: false, conditions: [] }
      },
      view: {
        admin: { value: false, conditions: [] },
        superadmin: { value: false, conditions: [] },
        user: { value: false, conditions: [] }
      },
      viewSettings: {
        admin: { value: false, conditions: [] },
        superadmin: { value: false, conditions: [] }
      }
    },
    showBeforeRegistration: false,
    title: '',
    titleTranslations: null,
    type: 'String',
    validation: {
      errorMessage: '',
      errorMessageTranslations: {},
      matchList: '',
      matchListEnableEditing: false,
      matchRegex: '',
      validateFunction: ''
    }
  } satisfies Omit<
    UserCustomAttribute,
    | 'id'
    | 'createdAt'
    | 'updatedAt'
    | 'position'
    | 'userAttributesFormSettings'
    | 'validation'
  > & {
    validation: Omit<UserCustomAttribute['validation'], 'matchList'> & {
      matchList: string;
    };
  };

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonIcon="plus"
      buttonSize="header"
      buttonText={messages.attributesAdd}
      contentStyle={{ padding: 0 }}
      formAllowEmptyStrings={true}
      initialValues={initialValues}
      modalActionText={messages.commonAdd}
      modalTitle={messages.attributesAdd}
      modalWidth="wide"
      onSubmit={handleSubmit}
    >
      {() => (
        <UserAttributesAttributeForm
          userCustomAttributes={userCustomAttributes}
        />
      )}
    </ModalButtonWithForm>
  );
};

export default UserAttributesAdd;
